import { VoidCallback1 } from '@deqo/foundation';
import { formatDistanceToNow } from 'date-fns';
import { darken } from 'polished';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import { Arr } from '../../../CodesForFoundation';
import { promptName, useObserver, useObserverReturningStore, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { FauxCopy } from '../../../concepts/text/FauxCopy';
import { useWindowSizeInRem } from '../../../platform/Hook';
import { SessionStorageService } from '../../../platform/StorageService';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Button, Colors, Float, Grid, Prompt, remInPx, usePrompt } from '../../../ui';
import { BetterDropButton } from '../../../ui/DropButton';
import { useMutationObserver } from '../../../ui/Hooks';
import { BetterPopArrow } from '../../../ui/PopArrow';
import { FixedScroller, ScrollerInteral } from '../../../ui/Scrollers';
import { Tip } from '../../../ui/Tip';
import { Emoji, EmojiStore } from '../../../ui/emoji';
import { useResolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { RelativeTo } from '../../canvas/entities/Physical';
import { TextSettings } from '../../canvas/entities/TextConventions';
import { ParticipantCounter } from '../../canvas/participants/Counter';
import { Participant } from '../../canvas/participants/Participant';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { BlockWheel } from '../../canvas/tooling/ToolLayer';
import { PopperInner, PopperMount, PopperPortal } from '../../canvas/ui/PopperElements';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { TagSupport } from '../annotations/TagSupport';
import { CreatedIncognitoAttr } from '../incognito/IncognitoAssociations';
import { IncognitoInfo } from '../incognito/IncognitoInfo';
import { QuillCommitSource, QuillInput } from '../mentions/QuillInput';
import { Reaction } from '../reactions/Reaction';
import { Comment } from './Comment';
import { CommentOpenAttr } from './CommentAssociations';
import { CommentAtsertView, useAtsertDongle } from './CommentAtsertView';
import { commentsAdd, commentsClose, commentsDelete } from './CommentCommands';
import { submitMentions } from './MentionCommands';
import { ReactionGroup } from './ReactionGroup';

const EmojiItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    width: 2.5rem;
    height: 2.5rem;
    
    &:hover {
        background: ${Colors.hover};
    }
    &:active {
        background: ${Colors.active};
    }
`;

const availableReactions = [
    ':slightly_smiling_face:',
    ':slightly_frowning_face:',
    ':laughing:',
    ':thinking_face:',
    ':thumbsup:',
    ':thumbsdown:',
    ':tada:',
    ':eyes:',
];

const AddReactionButton = ({ comment }: { comment: Comment }) => {

    const me = useResolve(ProfileModel);
    const objects = useResolve(ObjectManager);
    const onAdd = useCallback((idOrCode: string) => {
        objects.update(u => {
            u.set('history', 'skip');
            const emoji = EmojiStore.find(idOrCode);
            const nr = u.create(Reaction);
            nr.x = 0;
            nr.y = 0;
            nr.value = emoji.id;
            nr.userId = me.id;
            comment.add(nr);
        });

    }, []);

    const [open, setOpen] = useState(false);

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'top-end',
    });

    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);
    const pn = promptName();

    return <>
        <Tip text="Reaction">
            <BetterDropButton ref={setRefElmt} className={`ignore-${pn}`} active={open} onClick={onToggle}>
                <Icon size="1.25" src="deqo://icons/hero/emoji-happy" />
            </BetterDropButton>
        </Tip>
        {open && <PopperPortal>
            <PopperMount ref={setPopElmt} className="ignore-comment" style={styles.popper} {...attributes.popper}>
                <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                    <PopperInner direction="up" offset=".5">
                        <Float shadow="lg" inline>
                            <Grid columns={4}>
                                {availableReactions.map(r =>
                                    <EmojiItem key={r} onClick={() => onAdd(r)}>
                                        <Emoji idOrCode={r} />
                                    </EmojiItem>
                                )}
                            </Grid>
                        </Float>
                    </PopperInner>
                </Prompt>
            </PopperMount>
        </PopperPortal>}
    </>;
};

const CommentUser = styled.div`
    font-size: .875rem;
    font-weight: 500;
    color: ${Colors.textLighter};
`;

const CommentDate = styled.div`
    font-size: .875rem;
    color: ${Colors.textLighter};
`;

const CommentBody = styled.div`
    position: relative;
    z-index: 0;
    font-size: 1rem;
    color: ${Colors.text};

    textarea {
        width: 100%;
        background: ${Colors.l1};
        border-radius: .25rem;
        cursor: pointer;
        padding: .375rem;
        border: solid .125rem ${Colors.l1};

        &:hover {
            border: solid .125rem ${darken(.1, Colors.l1)};
        }
        &:focus {
            cursor: text;
            border: solid .125rem ${darken(.2, Colors.l1)};
        }
    }
`;

const CommentInput = styled.div`
    position: relative;
    min-height: 2.25rem;

    .ql-editor {

        background: ${Colors.l1};
        border: solid .125rem ${Colors.l1};
        border-radius: .25rem;

        &:hover {
            border: solid .125rem ${darken(.1, Colors.l1)};
        }
        &:focus {
            border: solid .125rem ${darken(.2, Colors.l1)};
        }
    }
`;

const CommentActions = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: .375rem;
    color: ${Colors.textLight};
    opacity: 0;
`;

const CommentFrame = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 1rem;

    &:hover {
        ${CommentActions} {
            opacity: 1;
        }
    }
`;

const CommentItemView = ({ comment, onEdit }: { comment: Comment, onEdit: VoidCallback1<Comment> }) => {

    const me = useResolve(ProfileModel);
    const ticker = useComponentObserver(comment);
    const reactions = useMemo(() => Arr.groupBy(Array.from(comment.polarize(Reaction)), 'value'), [ticker]);

    const pm = useResolve(ParticipantManager);
    const dispatch = useResolve(CommandDispatcher);
    const onDelete = useCallback(() => void (dispatch(commentsDelete, [comment.id])), [comment.id]);

    // We need to explicitly support incognito mode here by checking if the comment was made incognito and not showing the user (us).  This
    // is only relevant for comments created by the current user, as the audit data is redirect to local storage but still there, so but
    // without doing this it will show up as "us" and it would be confusing, like this comment.  Ask Steve if this doesn't make sense.
    const ptpt = useObserver(pm.getOrFallback(CreatedIncognitoAttr.get(comment) ? null : comment.userId));

    return <CommentFrame>
        <Flexer shrink>
            <ParticipantCounter info={ptpt} size={2.5} />
        </Flexer>
        <Flexer grow flex="column" padding=".125 0">
            <Flexer flex="row" align="center" justify="space-between">
                <Flexer flex="row" align="center">
                    <CommentUser>{ptpt.name}</CommentUser>
                    {!!comment.created && <Tip text={comment.created.toISOString()}>
                        <CommentDate>&nbsp;&bull;&nbsp;{formatDistanceToNow(comment.created)} ago</CommentDate>
                    </Tip>}
                </Flexer>
            </Flexer>
            <Flexer height=".5" />
            <FauxCopy value={comment.content} />
            <Flexer flex="row" gap=".25" style={{ flexWrap: 'wrap' }}>
                {Object.entries(reactions).map(([emoji, list]) =>
                    <ReactionGroup key={emoji} emoji={emoji} reactions={list} />
                )}
            </Flexer>
        </Flexer>
        <CommentActions>
            <AddReactionButton comment={comment} />
            {comment.userId === me.id && <>
                <Tip text="Edit">
                    <BetterDropButton onClick={() => onEdit(comment)}>
                        <Icon size="1.25" src="deqo://icons/hero/pencil" />
                    </BetterDropButton>
                </Tip>
                <Tip text="Delete">
                    <BetterDropButton onClick={onDelete}>
                        <Icon size="1.25" src="deqo://icons/hero/trash" />
                    </BetterDropButton>
                </Tip>
            </>}
        </CommentActions>
    </CommentFrame>;
};

const quillSettings = TextSettings.from({
    ...TextSettings.default,
    textMargin: remInPx(.375),
});

type CommentWriteViewProps = { subject: Component, onComplete: (added: boolean, newId?: string) => void };
const CommentWriteView = React.memo(({ subject, onComplete }: CommentWriteViewProps) => {

    const dispatch = useResolve(CommandDispatcher);
    const ptpts = useResolve(ParticipantManager);
    const incognito = useResolve(EditorSession).get(IncognitoInfo);
    const tempData = useResolve(SessionStorageService);
    const self = incognito.enabled ? Participant.unknown : ptpts.getSelf();
    const [value, setValue] = useState('');
    const ufkey = `deqo/editor/comment/${subject.globalId}`;
    const atsert = useObserverReturningStore(useAtsertDongle());

    // Restore unfinished
    useEffect(() => {
        const unfinished = tempData.get(ufkey);
        if (unfinished) {
            setValue(unfinished);
        }
    }, []);

    // Save unfinished
    useEffect(() => {
        if (!!value) {
            tempData.put(ufkey, value);
        }
        else {
            tempData.delete(ufkey);
        }
    }, [value]);

    const onCommit = useCallback((final: string, source: QuillCommitSource) => {
        if (source !== 'enter') return;
        tempData.delete(ufkey);
        if (final) {
            const commentId = dispatch(commentsAdd, [subject.id, final]) as string;
            onComplete(true, commentId);
        }
        else {
            onComplete(false);
        }
    }, [value, onComplete]);

    const onSave = useCallback(() => {
        onCommit(value, 'enter');
    }, [value, onCommit]);

    const onCancel = useCallback(() => {
        tempData.delete(ufkey);
        onComplete(false);
    }, []);

    return <CommentFrame>
        <Flexer shrink>
            <ParticipantCounter info={self} size={2.5} />
        </Flexer>
        <Flexer grow flex="column" gap=".5" padding=".1875 .5 .5 0">
            <CommentBody>
                <CommentInput>
                    <QuillInput
                        atsert={atsert}
                        autoFocus
                        enterMode="commit"
                        formatting={['bold', 'italic', 'list', 'mention', 'link']}
                        initialValue={value}
                        onChange={setValue}
                        onCommit={onCommit}
                        placeholder='Write a comment...'
                        textSettings={quillSettings}
                    />
                </CommentInput>
                <CommentAtsertView {...atsert.getState()} />
            </CommentBody>
            <Flexer flex="row" justify="flex-end" gap=".5">
                <Button size="md" variant="primary" onAction={() => onSave()}>Save</Button>
                <Button size="md" variant="common" onAction={() => onCancel()}>Cancel</Button>
            </Flexer>
        </Flexer>
    </CommentFrame>;
});

const CommentEditView = ({ comment, onComplete }: { comment: Comment, onComplete: (updated: boolean, commentId?: string) => void }) => {

    useComponentObserver(comment);

    const objects = useResolve(ObjectManager);
    const ptpt = useObserver(useResolve(ParticipantManager).get(comment.userId) || Participant.unknown);
    const [value, setValue] = useState(comment.content || '');
    const atsert = useObserverReturningStore(useAtsertDongle());

    const onCommit = useCallback((final: string, source: QuillCommitSource) => {
        if (source !== 'enter') return;
        if (final) {
            objects.update(u => {
                u.rolling(`comment/${comment.id}`);
                comment.content = value;
                comment.edited = true;
            });
            onComplete(true, comment.id);
        }
        else {
            onComplete(false);
        }
    }, [value, onComplete]);

    const onSave = useCallback(() => {
        onCommit(value, 'enter');
    }, [value, onCommit]);

    const onCancel = useCallback(() => {
        onComplete(false);
    }, [onComplete]);

    return <CommentFrame>
        <Flexer shrink>
            <ParticipantCounter info={ptpt} size={2.5} />
        </Flexer>
        <Flexer grow flex="column" gap=".5">
            <Flexer flex="row" align="center" justify="space-between" >
                <Flexer flex="row" align="center">
                    <CommentUser>{ptpt.name}</CommentUser>
                    {!!comment.created && <Tip text={comment.created.toISOString()}>
                        <CommentDate>&nbsp;&bull;&nbsp;{formatDistanceToNow(comment.created)} ago</CommentDate>
                    </Tip>}
                </Flexer>
            </Flexer>
            <CommentBody>
                <CommentInput>
                    <QuillInput
                        atsert={atsert}
                        autoFocus
                        enterMode="commit"
                        formatting={['bold', 'italic', 'list', 'mention', 'link']}
                        initialValue={value}
                        onChange={setValue}
                        onCommit={onCommit}
                        placeholder='Write a comment...'
                        textSettings={quillSettings}
                    />
                </CommentInput>
            </CommentBody>
            <Flexer flex="row" justify="flex-end" gap=".5">
                <Button size="md" variant="primary" onAction={onSave}>Save</Button>
                <Button size="md" variant="common" onAction={onCancel}>Cancel</Button>
            </Flexer>
        </Flexer>
    </CommentFrame>;
};

const CommentArrow: LFC<{ frame: React.MutableRefObject<HTMLDivElement>, subject: TagSupport }> = ({ frame, subject }) => {

    const camera = useResolve(Camera);

    // Use blooble to force rerender on change to ui
    const [_, setBlooble] = useState(uuid());
    useMutationObserver(frame, () => setBlooble(uuid()));

    const badge = subject.layout.tags.comments;
    if (!badge) return null;

    const wbs = Rect.fromElement(frame.current);
    // All relative to view:
    // Widget bounds
    const wb = camera.toViewFromScreen(Rect.fromElement(frame.current));
    // Badge position
    const bp = camera.toView(subject.position(RelativeTo.Model).add(badge.center()));
    // Arrow position
    const ap = Polyline.from(wb, true).nearestPointTo(bp);

    // Arrow position relative to widget bounds, we clamp here to some values inside the
    // widget bounds to stop the arrow going out of the edges...
    const apr = ap.subtract(wb[0]).floor()
        .clamp(new Point(0, remInPx(2)), new Point(wbs.width, wbs.height - remInPx(2)));

    const placement = (apr.x > 0) ? 'left' : 'right';
    return <BetterPopArrow placement={placement} color="#fff" border={[2, Colors.mystery]} size="1.5" style={apr.toCSS()} />;
};

const CommentWidgetFrame: FC<{ subject: TagSupport, onClose(): void, children: ReactNode }> = ({ subject, onClose, children }) => {
    const frameRef = useRef<HTMLDivElement>();
    const promptRef = usePrompt({
        name: 'comment',
        ignoreEnter: true,
        onAction: onClose,
    });
    return <BlockWheel>
        <Flexer ref={promptRef} width="32">
            <Float shadow="xl">
                <Flexer ref={frameRef} flex="column" gap="1" border={`solid 2px ${Colors.mystery}`} borderRadius={.375} padding=".5 .5 .5 1">
                    {children}
                </Flexer>
                {frameRef.current && <CommentArrow {...{ frame: frameRef, subject }} />}
            </Float>
        </Flexer>
    </BlockWheel>;
};

export const CommentWidget: LFC<{ widget: PopoutWidget }> = ({ widget }) => {
    const subject = widget.subject as TagSupport;

    const dispatch = useResolve(CommandDispatcher);
    const ticker = useComponentObserver(subject);
    const thread = useMemo(() => Arr.sortBy(subject.polarize(Comment), 'created', 'asc'), [ticker]);
    const [editing, setEditing] = useState(null as Comment);

    // Do to quill stuff we need to force a new instance when the write view is updated
    const [writeTicker, setWriteTicker] = useState(0);

    const { height } = useWindowSizeInRem();
    const scroller = useRef<ScrollerInteral>();

    const scrollToBottom = () => {
        if (scroller.current) {
            const se = scroller.current.getScrollElement();
            se.scrollTo(0, se.scrollHeight);
        }
    };

    useEffectOnce(() => void (CommentOpenAttr.setMaybe(subject, true)));
    useEffect(scrollToBottom, [scroller.current]);

    const onClose = useCallback(() => {
        CommentOpenAttr.set(subject, false);
        dispatch(commentsClose);
    }, [subject]);

    const onCompleteWriteNew = useCallback((added: boolean, newId?: string) => {
        if (!added) {
            onClose();
        }
        else {
            scrollToBottom();
            dispatch(submitMentions, [[newId]]);
        }
        setWriteTicker(t => t + 1);
    }, [scroller.current, thread, onClose]);

    const onCompleteWriteExisting = useCallback((updated: boolean, commentId?: string) => {
        setEditing(null);
        if (updated) {
            dispatch(submitMentions, [[commentId]]);
        }
    }, []);

    return <CommentWidgetFrame subject={subject} onClose={onClose}>
        {!!thread.length && <FixedScroller internalRef={scroller} autoHide={false} maxHeight={height * .6}>
            <Flexer flex="column" gap="1" padding=".5 1 0 0" minHeight="5.25">
                {thread.map(x => (editing !== x)
                    ? <CommentItemView key={x.id} comment={x} onEdit={setEditing} />
                    : <CommentEditView key={x.id} comment={x} onComplete={onCompleteWriteExisting} />
                )}
            </Flexer>
        </FixedScroller>}
        {!editing && <CommentWriteView key={writeTicker} {...{ subject }} onComplete={onCompleteWriteNew} />}
        {!!editing && <Flexer height="1" />}
    </CommentWidgetFrame>;
};
